import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Eye, Users, Briefcase, FileText, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyJob = { title: '', job_type: 'full-time', location: '', salary_range: '', description: '', responsibilities: '', requirements: '', benefits: '', status: 'active' };

const AdminJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showApps, setShowApps] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(emptyJob);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    if (!getToken()) { navigate('/admin'); return; }
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setJobs(res.data);
    } catch { toast.error('Failed to fetch jobs'); }
    finally { setLoading(false); }
  };

  const fetchApplications = async (jobId) => {
    try {
      const url = jobId ? `${API}/admin/job-applications?job_id=${jobId}` : `${API}/admin/job-applications`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      setApplications(res.data);
      setSelectedJobId(jobId);
      setShowApps(true);
    } catch { toast.error('Failed to fetch applications'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingJob) {
        await axios.put(`${API}/admin/jobs/${editingJob.id}`, formData, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success('Job updated!');
      } else {
        await axios.post(`${API}/admin/jobs`, formData, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success('Job created!');
      }
      setShowForm(false);
      setEditingJob(null);
      setFormData(emptyJob);
      fetchJobs();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({ title: job.title, job_type: job.job_type, location: job.location, salary_range: job.salary_range, description: job.description, responsibilities: job.responsibilities, requirements: job.requirements, benefits: job.benefits, status: job.status });
    setShowForm(true);
  };

  const handleDelete = async (jobId, title) => {
    if (!window.confirm(`Delete job "${title}"? This will also delete all applications.`)) return;
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Job deleted');
      fetchJobs();
    } catch { toast.error('Failed to delete'); }
  };

  const downloadResume = async (applicationId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/resume/${applicationId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'resume.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download resume'); }
  };

  const openNew = () => { setEditingJob(null); setFormData(emptyJob); setShowForm(true); };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button data-testid="back-to-dashboard" variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-slate-800"><ArrowLeft className="w-4 h-4" /></Button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Job Management</h1>
          </div>
          <div className="flex items-center space-x-2">
            <Button data-testid="view-all-apps" variant="ghost" size="sm" onClick={() => fetchApplications(null)} className="text-white hover:bg-slate-800"><Users className="w-4 h-4 mr-2" />Applications</Button>
            <Button data-testid="add-job-button" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 rounded-full" size="sm"><Plus className="w-4 h-4 mr-2" />Add Job</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? <div className="text-center py-12 text-slate-600">Loading...</div> : jobs.length === 0 ? (
          <Card className="border-slate-100"><CardContent className="py-12 text-center"><Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-600 mb-4">No jobs created yet</p><Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 rounded-full"><Plus className="w-4 h-4 mr-2" />Create First Job</Button></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {jobs.map(job => (
              <Card key={job.id} data-testid={`admin-job-${job.id}`} className="border-slate-100 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-bold text-slate-900">{job.title}</h3>
                        <Badge className={job.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{job.status}</Badge>
                        <Badge variant="outline">{job.job_type === 'full-time' ? 'Full-time' : 'Part-time'}</Badge>
                      </div>
                      <p className="text-sm text-slate-600">{job.location} | {job.salary_range} | {job.applications_count || 0} applications</p>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <Button data-testid={`apps-${job.id}`} variant="outline" size="sm" onClick={() => fetchApplications(job.id)} className="rounded-full"><Eye className="w-4 h-4 mr-1" />{job.applications_count || 0}</Button>
                      <Button data-testid={`edit-${job.id}`} variant="outline" size="sm" onClick={() => handleEdit(job)} className="rounded-full"><Edit className="w-4 h-4" /></Button>
                      <Button data-testid={`delete-${job.id}`} variant="destructive" size="sm" onClick={() => handleDelete(job.id, job.title)} className="rounded-full"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Job Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle><DialogDescription>Fill in the job details</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Job Title *</Label><Input data-testid="job-title-input" placeholder="e.g., Long Haul Driver" className="h-12 bg-slate-50" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Job Type *</Label>
                <Select value={formData.job_type} onValueChange={(v) => setFormData({...formData, job_type: v})}>
                  <SelectTrigger data-testid="job-type-select" className="h-12 bg-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="full-time">Full-time</SelectItem><SelectItem value="part-time">Part-time</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Location *</Label><Input data-testid="job-location-input" placeholder="e.g., Mumbai" className="h-12 bg-slate-50" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Salary Range *</Label><Input data-testid="job-salary-input" placeholder="e.g., 20,000 - 35,000/month" className="h-12 bg-slate-50" value={formData.salary_range} onChange={(e) => setFormData({...formData, salary_range: e.target.value})} required /></div>
            </div>
            <div className="space-y-2"><Label>Description *</Label><Textarea data-testid="job-description-input" placeholder="Job description..." className="min-h-[80px] bg-slate-50" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Responsibilities (one per line)</Label><Textarea data-testid="job-responsibilities-input" placeholder="- Drive assigned routes&#10;- Maintain vehicle logs" className="min-h-[80px] bg-slate-50" value={formData.responsibilities} onChange={(e) => setFormData({...formData, responsibilities: e.target.value})} /></div>
            <div className="space-y-2"><Label>Requirements (one per line)</Label><Textarea data-testid="job-requirements-input" placeholder="- Valid HMV license&#10;- 2+ years experience" className="min-h-[80px] bg-slate-50" value={formData.requirements} onChange={(e) => setFormData({...formData, requirements: e.target.value})} /></div>
            <div className="space-y-2"><Label>Benefits (one per line)</Label><Textarea data-testid="job-benefits-input" placeholder="- Health insurance&#10;- Fuel allowance" className="min-h-[80px] bg-slate-50" value={formData.benefits} onChange={(e) => setFormData({...formData, benefits: e.target.value})} /></div>
            {editingJob && (
              <div className="space-y-2"><Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="h-12 bg-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <div className="flex space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button type="submit" data-testid="save-job-button" className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full" disabled={saving}>{saving ? 'Saving...' : editingJob ? 'Update Job' : 'Create Job'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Applications Modal */}
      <Dialog open={showApps} onOpenChange={setShowApps}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Job Applications</DialogTitle><DialogDescription>{applications.length} application{applications.length !== 1 ? 's' : ''}</DialogDescription></DialogHeader>
          {applications.length === 0 ? <p className="text-center py-8 text-slate-600">No applications yet</p> : (
            <div className="space-y-3">
              {applications.map(app => (
                <div key={app.id} data-testid={`application-${app.id}`} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-semibold text-lg">{app.name}</h4>
                      <p className="text-sm text-slate-600">Phone: {app.phone} | Experience: {app.experience} | License: {app.license_type}</p>
                      <p className="text-sm text-slate-500">Applied for: {app.job_title}</p>
                      {app.resume_path && (
                        <Button
                          data-testid={`download-resume-${app.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => downloadResume(app.id, app.resume_filename)}
                          className="mt-2 text-blue-600 border-blue-200 hover:bg-blue-50 rounded-full"
                        >
                          <Download className="w-3 h-3 mr-1.5" />
                          {app.resume_filename || 'Download Resume'}
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{new Date(app.applied_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobs;
